import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Mail, Loader2, ArrowLeft } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "../admin/AuthContext";
import { LogoMark } from "../components/Logo";

const AdminLogin = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      const next = searchParams.get("next") || "/admin";
      navigate(next, { replace: true });
    } catch (err) {
      setError(err.message || "Login failed");
      setLoading(false);
    }
  };

  return (
    <div className="grain relative flex min-h-screen items-center justify-center overflow-hidden bg-navy px-5" data-testid="admin-login-page">
      <div className="bp-grid absolute inset-0" />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 w-full max-w-md border border-slate-800 bg-navy-800/60 p-8 backdrop-blur sm:p-10"
      >
        <LogoMark className="h-9 w-auto" dark />
        <h1 className="mt-6 font-display text-2xl font-extrabold tracking-tight text-white" data-testid="admin-login-title">
          Admin Dashboard
        </h1>
        <p className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">
          Universal Engineers · Phase 2 Panel
        </p>

        <form onSubmit={submit} className="mt-8 space-y-5" noValidate>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="a-email" className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">Email</label>
            <div className="flex items-center border border-slate-700 bg-navy/60 focus-within:border-safety">
              <Mail size={15} className="mx-3 shrink-0 text-slate-500" />
              <input
                id="a-email" type="email" value={email} autoComplete="username"
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@universalengineers.in"
                data-testid="admin-login-email-input"
                className="w-full bg-transparent py-2.5 pr-3 text-sm text-white placeholder:text-slate-600 focus:outline-none"
              />
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="a-password" className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">Password</label>
            <div className="flex items-center border border-slate-700 bg-navy/60 focus-within:border-safety">
              <Lock size={15} className="mx-3 shrink-0 text-slate-500" />
              <input
                id="a-password" type="password" value={password} autoComplete="current-password"
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="admin-login-password-input"
                className="w-full bg-transparent py-2.5 pr-3 text-sm text-white placeholder:text-slate-600 focus:outline-none"
              />
            </div>
          </div>

          {error && (
            <p className="border-l-2 border-safety bg-safety/10 px-4 py-3 text-sm text-orange-200" data-testid="admin-login-error" role="alert">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit-button"
            className="flex w-full items-center justify-center gap-2.5 bg-safety px-6 py-3.5 font-display text-sm font-bold uppercase tracking-[0.14em] text-white transition-colors hover:bg-white hover:text-navy disabled:opacity-60"
          >
            {loading ? <><Loader2 size={16} className="animate-spin" /> Signing in…</> : "Sign In"}
          </button>
        </form>

        <Link to="/" data-testid="admin-back-to-site-link" className="mt-8 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 hover:text-safety">
          <ArrowLeft size={13} /> Back to website
        </Link>
      </motion.div>
    </div>
  );
};

export default AdminLogin;
